import type { Editor } from "@tiptap/core";
import {
  buildDirectiveRaw,
  type ParsedDirectiveRaw,
} from "../../directives/core/parseRaw";
import type { DirectiveChipStrategy } from "../../directives/core/chipStrategy";
import type { DirectiveConfigItem } from "../../directives/core/config";
import type { Variables } from "@/lib/sparxs/metadataSchemas";
import {
  updateRawAtPos,
  focusEditorAfterNode,
} from "../variableTokenNodeViewHelpers";
import type { DirectiveChipEditSegment } from "../../types/directiveChipTypes";

type BooleanRef = { current: boolean };

type RefsSelectVariable = {
  justCompletedRef: BooleanRef;
  preventCloseOnceRef: BooleanRef;
  focusChoiceSelectOnceRef: BooleanRef;
  focusChoiceInputOnceRef: BooleanRef;
};

type RefsChoiceSubmit = {
  justCompletedRef: BooleanRef;
  preventCloseOnceRef: BooleanRef;
  focusChoiceSelectOnceRef: BooleanRef;
  focusChoiceInputOnceRef: BooleanRef;
};

type BaseDeps = {
  editor: Editor;
  getPos: () => number | undefined;
  parsed: ParsedDirectiveRaw | null;
  config: DirectiveConfigItem | null;
  allVariables: Variables[];
  strategy: DirectiveChipStrategy | null;
};

export const runSelectVariable = (
  variableName: string,
  deps: BaseDeps & RefsSelectVariable & { setOpen: (open: boolean) => void },
) => {
  const {
    editor,
    getPos,
    parsed,
    config,
    allVariables,
    strategy,
    justCompletedRef,
    preventCloseOnceRef,
    focusChoiceSelectOnceRef,
    focusChoiceInputOnceRef,
    setOpen,
  } = deps;
  if (parsed == null || config == null) return;
  const newRaw = buildDirectiveRaw(parsed.directive, variableName, []);
  const isCompleting =
    strategy != null
      ? strategy.isCompleteAfterVariableSelect(
          variableName,
          parsed,
          config,
          allVariables,
        )
      : config.choiceSlots.length === 0;
  if (isCompleting) justCompletedRef.current = true;
  if (!isCompleting) {
    preventCloseOnceRef.current = true;
    const parsedAfterVar: ParsedDirectiveRaw = {
      raw: "",
      directive: parsed.directive,
      variable: variableName,
      choices: [],
      isDirective: true,
      isComplete: false,
    };
    const nextSlot =
      strategy != null
        ? strategy.getNextChoiceSlot(
            parsedAfterVar,
            config,
            allVariables,
            0,
            [],
          )
        : config.choiceSlots[0];
    if (nextSlot?.options != null) {
      focusChoiceSelectOnceRef.current = true;
    } else {
      focusChoiceInputOnceRef.current = true;
    }
  }
  updateRawAtPos(editor, getPos, newRaw, {
    focus: isCompleting,
  });
  if (isCompleting) {
    setOpen(false);
    focusEditorAfterNode(editor, getPos);
  }
};

export const runChoiceSubmit = (
  value: string,
  deps: BaseDeps & RefsChoiceSubmit & { setOpen: (open: boolean) => void },
) => {
  const {
    editor,
    getPos,
    parsed,
    config,
    allVariables,
    strategy,
    justCompletedRef,
    preventCloseOnceRef,
    focusChoiceSelectOnceRef,
    focusChoiceInputOnceRef,
    setOpen,
  } = deps;
  if (parsed == null || config == null) return;
  const nextIndex = parsed.choices.length;
  const { slot, maxSlots } =
    strategy != null
      ? strategy.getSlotAndMaxForChoiceSubmit(
          parsed,
          config,
          allVariables,
          nextIndex,
        )
      : {
          slot: config.choiceSlots[nextIndex],
          maxSlots: config.choiceSlots.length,
        };
  const trimmed = value.trim();
  if (
    slot?.options != null &&
    trimmed !== "" &&
    !slot.options.some((o) => o.value === trimmed)
  ) {
    return;
  }
  const isOptionalSkip =
    strategy != null
      ? strategy.allowOptionalSkip(slot, value)
      : slot?.options != null && trimmed === "";
  const newChoices = isOptionalSkip
    ? parsed.choices
    : trimmed === ""
      ? parsed.choices
      : [...parsed.choices, trimmed];
  const newRaw = buildDirectiveRaw(
    parsed.directive,
    parsed.variable,
    newChoices,
  );
  const isCompleting = isOptionalSkip || newChoices.length >= maxSlots;
  if (!isCompleting) {
    preventCloseOnceRef.current = true;
    const parsedAfter = { ...parsed, choices: newChoices };
    const nextSlot =
      strategy != null
        ? strategy.getNextChoiceSlot(
            parsedAfter,
            config,
            allVariables,
            newChoices.length,
            newChoices,
          )
        : config.choiceSlots[newChoices.length];
    if (nextSlot?.options != null) {
      focusChoiceSelectOnceRef.current = true;
    } else {
      focusChoiceInputOnceRef.current = true;
    }
  }
  updateRawAtPos(editor, getPos, newRaw, {
    focus: isCompleting,
  });
  if (isCompleting) {
    justCompletedRef.current = true;
    setOpen(false);
    focusEditorAfterNode(editor, getPos);
  }
};

export const runSelectDirective = (
  directiveKey: string,
  deps: Pick<BaseDeps, "editor" | "getPos"> & {
    setEditSegment: (s: DirectiveChipEditSegment | null) => void;
    setOpen: (open: boolean) => void;
  },
) => {
  const { editor, getPos, setEditSegment, setOpen } = deps;
  const newRaw = `@${directiveKey}/`;
  updateRawAtPos(editor, getPos, newRaw);
  setEditSegment(null);
  setOpen(false);
};

export const runUpdateVariableOnly = (
  variableName: string,
  deps: Pick<BaseDeps, "editor" | "getPos" | "parsed"> & {
    setEditSegment: (s: DirectiveChipEditSegment | null) => void;
    setOpen: (open: boolean) => void;
  },
) => {
  const { editor, getPos, parsed, setEditSegment, setOpen } = deps;
  if (parsed == null) return;
  const newRaw = buildDirectiveRaw(
    parsed.directive,
    variableName,
    parsed.choices,
  );
  updateRawAtPos(editor, getPos, newRaw);
  setEditSegment(null);
  setOpen(false);
};

export const runUpdateChoiceOnly = (
  choiceIndex: number,
  value: string,
  deps: BaseDeps & {
    setEditSegment: (s: DirectiveChipEditSegment | null) => void;
    setOpen: (open: boolean) => void;
  },
) => {
  const {
    editor,
    getPos,
    parsed,
    config,
    allVariables,
    strategy,
    setEditSegment,
    setOpen,
  } = deps;
  if (parsed == null || config == null) return;
  const slot =
    strategy != null
      ? strategy.getChoiceSlotForEdit(
          parsed,
          config,
          allVariables,
          choiceIndex,
          parsed.choices,
        )
      : config.choiceSlots[choiceIndex];
  const trimmedEdit = value.trim();
  const isOptionalClear =
    strategy != null
      ? strategy.allowOptionalSkip(slot, value)
      : slot?.options != null && trimmedEdit === "";
  if (
    slot?.options != null &&
    trimmedEdit !== "" &&
    !slot.options.some((o) => o.value === trimmedEdit)
  ) {
    return;
  }
  const newChoices = isOptionalClear
    ? parsed.choices.filter((_, i) => i !== choiceIndex)
    : (() => {
        const next = [...parsed.choices];
        next[choiceIndex] = trimmedEdit;
        return next;
      })();
  const newRaw = buildDirectiveRaw(
    parsed.directive,
    parsed.variable,
    newChoices,
  );
  updateRawAtPos(editor, getPos, newRaw);
  setEditSegment(null);
  setOpen(false);
};

export const runReorderPrintTableChoices = (
  fromIndex: number,
  toIndex: number,
  deps: Pick<BaseDeps, "editor" | "getPos" | "parsed">,
) => {
  const { editor, getPos, parsed } = deps;
  if (parsed == null) return;
  if (fromIndex === toIndex) return;
  const next = [...parsed.choices];
  const [removed] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, removed);
  const newRaw = buildDirectiveRaw(parsed.directive, parsed.variable, next);
  updateRawAtPos(editor, getPos, newRaw);
};
